import { sanitizeText } from './codepages.js';

/**
 * Print documents → fixed-width rows.
 *
 * A document is { blocks: [...] } built by the templates (templates.js):
 *   { t: 'text', text, align?, bold?, w?, h?, invert?, underline? }   wrapped paragraph
 *   { t: 'pair', left, right, bold?, w?, h? }                           left text … right text
 *   { t: 'cols', cols: [{ text, w: n | '*', align? }], bold?, h? }      table row (gap of 1)
 *   { t: 'rule', ch? }                                                  full-width separator
 *   { t: 'blank', n? }
 *   { t: 'image', name, align? }                                        bitmap supplied at render time
 *   { t: 'qr', data, size?: 'small'|'medium'|'large', align? }
 *   { t: 'barcode', data }
 *
 * Every text row comes out padded to the exact line width, so alignment is done with spaces
 * (identical on every printer) and the on-screen preview is a faithful copy of the paper.
 */

export const chars = (s) => [...String(s ?? '')];
export const width = (s) => chars(s).length;

function padAlign(text, w, align = 'left') {
  const len = width(text);
  if (len >= w) return chars(text).slice(0, w).join('');
  const space = w - len;
  if (align === 'right') return ' '.repeat(space) + text;
  if (align === 'center') {
    const left = Math.floor(space / 2);
    return ' '.repeat(left) + text + ' '.repeat(space - left);
  }
  return text + ' '.repeat(space);
}

/**
 * Word-wrap to `w` columns; words longer than a line are split. Keeps explicit newlines and
 * leading indentation (continuation lines are indented the same way).
 */
export function wrap(text, w) {
  const out = [];
  for (const para of String(text ?? '').split('\n')) {
    const indent = Math.min(para.match(/^ */)[0].length, Math.floor(w / 2));
    const pad = Array(indent).fill(' ');
    const room = w - indent;
    let line = [];
    const flush = () => out.push([...pad, ...line].join(''));
    for (const word of para.split(/ +/).filter(Boolean)) {
      let wc = chars(word);
      if (line.length && line.length + 1 + wc.length <= room) {
        line.push(' ', ...wc);
        continue;
      }
      if (line.length) flush();
      line = [];
      while (wc.length > room) {
        line = wc.slice(0, room);
        flush();
        wc = wc.slice(room);
      }
      line = wc;
    }
    if (line.length || !out.length || para === '') flush();
  }
  return out;
}

const QR_MODULE = { small: 4, medium: 6, large: 8 };

export function layoutDocument(doc, profile, { rasterText = profile.rasterText } = {}) {
  const cpl = profile.charsPerLine;
  const prep = (t) => (rasterText ? String(t ?? '') : sanitizeText(t, profile));
  const rows = [];
  const textRow = (text, b, w) =>
    rows.push({
      k: 'text',
      text,
      cols: w,
      w: b.w ?? 1,
      h: b.h ?? 1,
      bold: Boolean(b.bold),
      invert: Boolean(b.invert),
      underline: Boolean(b.underline),
    });

  for (const b of doc.blocks ?? []) {
    if (!b) continue;
    const lineW = Math.max(1, Math.floor(cpl / (b.w ?? 1)));
    switch (b.t) {
      case 'text': {
        for (const line of wrap(prep(b.text), lineW)) textRow(padAlign(line, lineW, b.align), b, lineW);
        break;
      }
      case 'pair': {
        const left = prep(b.left);
        const right = prep(b.right);
        const rw = width(right);
        if (width(left) + 1 + rw <= lineW) {
          textRow(left + ' '.repeat(lineW - width(left) - rw) + right, b, lineW);
          break;
        }
        const lines = wrap(left, rw && rw < lineW - 4 ? lineW - rw - 1 : lineW);
        const last = lines.pop();
        for (const l of lines) textRow(padAlign(l, lineW), b, lineW);
        if (width(last) + 1 + rw <= lineW) textRow(last + ' '.repeat(lineW - width(last) - rw) + right, b, lineW);
        else {
          textRow(padAlign(last, lineW), b, lineW);
          textRow(padAlign(right, lineW, 'right'), b, lineW);
        }
        break;
      }
      case 'cols': {
        const cols = b.cols.filter(Boolean);
        const fixed = cols.reduce((s, c) => s + (c.w === '*' ? 0 : c.w), 0) + (cols.length - 1);
        const star = Math.max(1, lineW - fixed);
        const widths = cols.map((c) => (c.w === '*' ? star : c.w));
        const cells = cols.map((c, i) => wrap(prep(c.text), widths[i]));
        const height = Math.max(...cells.map((c) => c.length));
        for (let r = 0; r < height; r++) {
          const parts = cols.map((c, i) => padAlign(cells[i][r] ?? '', widths[i], c.align));
          textRow(padAlign(parts.join(' '), lineW), b, lineW);
        }
        break;
      }
      case 'rule':
        textRow((b.ch ?? '-').repeat(cpl), { ...b, w: 1, h: 1 }, cpl);
        break;
      case 'blank':
        for (let i = 0; i < (b.n ?? 1); i++) textRow(' '.repeat(cpl), {}, cpl);
        break;
      case 'image':
        rows.push({ k: 'image', name: b.name, bitmap: b.bitmap ?? null, align: b.align ?? 'center' });
        break;
      case 'qr':
        if (b.data) rows.push({ k: 'qr', data: b.data, module: QR_MODULE[b.size] ?? 6, align: b.align ?? 'center' });
        break;
      case 'barcode':
        if (b.data) rows.push({ k: 'barcode', data: String(b.data) });
        break;
      default:
        throw new Error(`Unknown print block "${b.t}"`);
    }
  }
  return rows;
}

/** Plain-text rendering of the rows (logs, tests, the agent's console preview). */
export function rowsToText(rows) {
  return rows
    .map((r) => {
      if (r.k === 'text') return r.text.replace(/\s+$/, '');
      if (r.k === 'image') return `[image ${r.name ?? ''}]`;
      if (r.k === 'qr') return `[QR ${r.data}]`;
      if (r.k === 'barcode') return `[barcode ${r.data}]`;
      return '';
    })
    .join('\n');
}
